/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { createContext, useEffect, useReducer } from "react";
import axios, { AxiosError } from "axios";
import PropTypes from "prop-types";
import { useIsAuthenticated } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import Config from "data/config";
import { LOGIN, LOGOUT } from "data/store/reducers/auth/auth.actions";
import { authReducer } from "data/store/reducers/auth/auth";
import Loader from "components/Loader";
import { removeAccountItem, setAccountItem, setUserRoles } from "data/store/reducers/account/account.slice";
import { setOrganizationItem } from "data/store/reducers/organization";
import { Logger } from "cms/utils/log/logger";

const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    token: null,
};

const TOKEN = SimpliCityAdminSDK.utils.TOKEN;
const { INVALID, EXPIRED, NEEDS_REFRESH, VALID } = TOKEN;
const AzureADContext = createContext(null);

export const AzureADProvider = (props) => {

    const { children } = props;
    const [state, dispatch] = useReducer(authReducer, initialState);
    const [cookies, setCookie, removeCookie] = useCookies("simplicity_admin");
    const isAuthenticated = useIsAuthenticated();
    const navigate = useNavigate();
    const dispatcher = useDispatch();

    const verifyToken = (serviceToken) => {
        if (!serviceToken) {
            return false;
        }
        const decoded = jwtDecode(serviceToken);
        return decoded.exp > Date.now() / 1000;
    };

    const verifySession = async () => {
        /* ---------------------------------------------------------
            STATES: access token / refresh token :
               - valid    /    valid   -> do nothing
               - near expiry / valid   -> refresh token
               - expired   /   valid   -> refresh token
               - expired   /   expired -> logout
          --------------------------------------------------------- */
        try {
            const serviceToken = cookies["simplicity_admin"];
            // Logger.info(`init :: Verify Session: ${serviceToken}`)
            let isValid = false;
            let status = INVALID;
            const now = Date.now();
            let token_exp;
            // if we have an access token, decoded its expiry
            if (serviceToken) {
                // Logger.info(`Found token stored as 'simplicity_admin' cookie. Verifying.`)
                const decoded = jwtDecode(serviceToken);
                token_exp = decoded.exp * 1000;
                const refresh_point = token_exp - 60 * 5 * 1000; // 5 mins before
                if (now > token_exp) {
                    status = EXPIRED;
                    Logger.info(`[AzureADProvider.verifySession] Token: ${EXPIRED}`);
                } else if (now > refresh_point) {
                    status = NEEDS_REFRESH;
                    Logger.info(`[AzureADProvider.verifySession] Token Refresh: ${NEEDS_REFRESH}`);
                } else {
                    status = VALID;
                    // Logger.info(`Token: ${TOKEN.VALID}`)
                }
                const nowtime = now / 1000;
                isValid = decoded.exp > nowtime;
            } else {
                Logger.info("[AzureADProvider.verifySession] No Token", { serviceToken });
            }
            // const format = 'h:mm:ss a'
            // const nowFormatted = dayjs(now).format(format)
            // const expiryFormatted = dayjs(token_exp).format(format)

            if (status === NEEDS_REFRESH && isValid) {
                await attemptRefresh();

            } else if (!isValid) {
                // Login session NOT ok - REDIRECT TO AZURE LOGIN
                // const not_valid_msg = `[AzureADProvider.verifySession] session was not valid.  ${nowFormatted}  -  ${expiryFormatted}` 
                // const data = { serviceToken, isValid, nowFormatted, expiryFormatted, status}
                // Logger.info(not_valid_msg, data)
                dispatch({
                    type: LOGOUT,
                    payload: {
                        isLoggedIn: false,
                        isInitialized: true,
                    },
                });
                dispatcher(removeAccountItem());
                navigate("/Welcome");
                status = false;
                return status;

            } else {
                // const valid_msg = `[AzureADProvider.verifySession] session was valid.  [now:${nowFormatted}]  [expires:${expiryFormatted}]`
                // const data = { serviceToken, isValid, nowFormatted, expiryFormatted, status}
                // Logger.info( valid_msg, data)
                status = true;
                return status;
            }
        } catch (err) {
            Logger.error(err);
        }
    };

    const setSession = (accessToken, refreshToken) => {
        if (refreshToken) {
            // Logger.debug('SET REFRESH TOKEN: ' + accessToken)
            setCookie("simplicity_refresh", refreshToken, {
                path: "/",
                domain: Config.COOKIE_DOMAIN,
            });
        }
        if (accessToken) {
            // Logger.debug('SET ACCESS TOKEN: ' + accessToken)
            setCookie("simplicity_admin", accessToken, {
                path: "/",
                domain: Config.COOKIE_DOMAIN,
            });
            // localStorage.setItem('serviceToken', serviceToken);
            axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        } else {
            // Logger.debug('SET SERVICE TOKEN: MISSING')
            removeCookie("simplicity_admin", {
                path: "/",
                domain: Config.COOKIE_DOMAIN,
            });
            delete axios.defaults.headers.common.Authorization;
        }
    };

    // Session Verification Checker
    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = cookies["simplicity_admin"];
                // const serviceRefreshToken = cookies['simplicity_refresh']

                // Logger.debug(`init :: Verify token: ${serviceToken}`)
                // Logger.debug(`init :: Refresh token: ${serviceRefreshToken}`)
        
                let account;
                // const tokenStatus = SimpliCityAdminSDK.utils.checkTokenExpiry(serviceToken)
                // Logger.debug(`TOKEN STATUS: ${tokenStatus}`)
        
                if (serviceToken && verifyToken(serviceToken)) {
                    // Logger.debug(`Running init (check session)`)
            
                    try {
                        account = await SimpliCityAdminSDK.account.me(serviceToken);
                        // Logger.log(account)
                    } catch (err) {
                        Logger.error(`Request to get profile Failed: ${Config.API_BASEURL}`);
                        Logger.error(err);
                        if (err instanceof AxiosError) {
                            Logger.error(`Network Error - ${err.code} ${err.message}`);
                            // causes loop
                            // alert(`Login Token Expired.`)
                            // window.location.href = '/Welcome'
                            dispatch({
                                type: LOGIN,
                                payload: {
                                    isLoggedIn: false,
                                    isInitialized: true
                                },
                            });
                        }
                    }
                    try {
                        const { guid: user_id } = account;
                        const roles = await SimpliCityAdminSDK.role.findRolesForProfile(
                            serviceToken, user_id
                        );
                        dispatcher(setUserRoles(roles));
                    } catch (err) {
                        Logger.error("Request to get roles failed");
                    }

                    if (!account) {
                        dispatch({
                            type: LOGOUT,
                            payload: {
                                isLoggedIn: false,
                                isInitialized: true,
                            },
                        });
                        dispatcher(removeAccountItem());
                        navigate("/Welcome");
                    } else {
                        dispatcher(setAccountItem(account));

                        const org_guid = account.organization;
                        const organization = await SimpliCityAdminSDK.organization.findOne(serviceToken, org_guid);
                        dispatcher(setOrganizationItem(organization));

                        dispatch({
                            type: LOGIN,
                            payload: {
                                isLoggedIn: true,
                                isInitialized: true,
                                user: account,
                                account,
                                token: serviceToken,
                                organization,
                            },
                        });
                    }
                } else {
                    // Login session NOT ok
                    await attemptRefresh();

                }
            } catch (err) {
                Logger.error("failed to restore tokens.");
                Logger.error(err);
                // Network Error possible if API not responding
                const url = new URL(window.location.href);
                const path = url.pathname;
                if (err && err.name === "AxiosError" && err?.code === "ERR_NETWORK" && path !== "/Welcome") {
                    // Replace this with a better UI
                    alert("Network Error");
                    window.location.href = "/Welcome";
                }
                Logger.error(err.message);
                dispatch({
                    type: LOGOUT,
                    payload: {
                        isLoggedIn: false,
                        isInitialized: true,
                    },
                });

            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // ATTEMPT TO REFRESH ACCESS TOKEN VIA REFRESH
    const attemptRefresh = async () => {
        const old_access_token = cookies["simplicity_admin"];
        const old_refresh_token = cookies["simplicity_refresh"];
        
        const result = await SimpliCityAdminSDK.account.verifyToken(old_access_token, old_refresh_token);
        const status = result?.status;
        Logger.info(`[attemptRefresh] ${status}`, result);
        const access_token = result?.access_token;
        const refresh_token = result?.refresh_token;
        // const id_token = result?.id_token
        // const scope = result?.scope
        // const token_type = result?.token_type
        // const expires_in = result?.expires_in
        // const ext_expires_in = result?.ext_expires_in
        // const display_expires = result?.display_expires
        if (access_token) {
            Logger.info("Refresh token used to renew session");
            Logger.info("setting cookies");
            // setSession(access_token, refresh_token)
            setCookie("simplicity_refresh", refresh_token, {
                path: "/",
                domain: Config.COOKIE_DOMAIN,
            });
            setCookie("simplicity_admin", access_token, {
                path: "/",
                domain: Config.COOKIE_DOMAIN,
            });
            // reloading may be needed here, but also painful for users.
            window.location.reload();
        } else {
            Logger.info("Failed to refresh access token using refresh token. Redirect to /Welcome.");
            // REDIRECT TO AZURE LOGOUT
            dispatch({
                type: LOGOUT,
                payload: {
                    isLoggedIn: false,
                    isInitialized: true,
                },
            });
            dispatcher(removeAccountItem());
            navigate("/Welcome");
        }
    };
    
    const logout = () => {
        Logger.info("logout taking place");
        removeCookie("simplicity_admin", { path: "/", domain: Config.COOKIE_DOMAIN });
        removeCookie("simplicity_id", { path: "/", domain: Config.COOKIE_DOMAIN });
        removeCookie("simplicity_refresh", { path: "/", domain: Config.COOKIE_DOMAIN });
        setSession();
        // dispatch({type: LOGOUT})
        const url = `${Config.API_BASEURL}${Config.LOGOUT_URL}`;
        Logger.info(`Logout: ${url}`);
        window.location.replace(url);
    };

    const getToken = () => {
        const serviceToken = cookies["simplicity_admin"];
        return serviceToken;
    };

    // const requestToken = async () => {
    //     const msalConfig = {
    //         auth: {
    //             clientId: Config.Azure.APP_AZURE_CLIENT_ID,
    //             authority: Config.Azure.AZURE_AUTHORITY,
    //             redirectUri: Config.Azure.AZURE_REDIRECT_URI,
    //         },
    //         cache: {
    //             cacheLocation: "localStorage", // This configures where your cache will be stored
    //             storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
    //         }
    //     }
    //     // Request access token - may cause interactive login
    //     const access_token = await SimpliCityAdminSDK.account.requestAccessToken(msalConfig)
    //     Logger.debug('ACCESS TOKEN',access_token)
    //
    //     const account = await SimpliCityAdminSDK.account.me(access_token)
    //     Logger.debug('Account: ', account)
    //     dispatcher(setAccountItem(account))
    //     dispatch({
    //         type: LOGIN,
    //         payload: {
    //             isLoggedIn: true,
    //             isInitialized: true,
    //             user: account,
    //             account,
    //             token: access_token
    //         }
    //     })
    //     setSession(access_token)
    //
    //     return access_token
    // }

    const requestToken = async () => {
        // Use refresh token to request new access token
        const { access_token, refresh_token } = await SimpliCityAdminSDK.account.verifyToken(
            cookies["simplicity_admin"],
            cookies["simplicity_refresh"],
        );
        // Logger.debug('ACCESS TOKEN',access_token)
        // Logger.debug('REFRESH TOKEN',refresh_token)
        setSession(access_token, refresh_token);

        const account = await SimpliCityAdminSDK.account.me(access_token);
        Logger.info("Account: ", account);
        dispatcher(setAccountItem(account));
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                isInitialized: true,
                user: account,
                account,
                token: access_token,
            },
        });
        return access_token;
    };

    // const authApp = new Msal.PublicClientApplication(msalConfig)
    // // TODO: Move to sdk
    // const requestAccessToken = async function () {
    //
    //     const activeAccount = authApp.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    //     const accounts = authApp.getAllAccounts();
    //     const accessTokenRequest = {
    //         ...loginRequest,
    //         account: accounts[0]
    //     };
    //
    //     if (!activeAccount && accounts.length === 0) {
    //         /*
    //         * User is not signed in. Throw error or wait for user to login.
    //         * Do not attempt to log a user in, outside of the context of MsalProvider
    //         */
    //         // const authResult = await authApp.acquireTokenInteractive(request);
    //     }
    //
    //     let accessToken
    //
    //     // const request = { scopes: ["User.Read"], account: activeAccount || accounts[0]}
    //     // const authResult = await authApp.acquireTokenSilent(accessTokenRequest);
    //     // const authResult = await instance.acquireTokenInteractive(request);
    //     authApp.acquireTokenSilent(accessTokenRequest)
    //         .then((accessTokenResponse) => {
    //             // Acquire token silent success
    //             accessToken = accessTokenResponse.accessToken;
    //             Logger.debug('silent',accessToken)
    //             return accessToken
    //             // Call your API with token
    //             // callApi(accessToken).then((response) => {
    //             //     setApiData(response);
    //             // });
    //         })
    //         .catch((error) => {
    //             if (error instanceof InteractionRequiredAuthError) {
    //                 authApp.acquireTokenPopup(accessTokenRequest)
    //                 .then((accessTokenResponse) => {
    //                     // Acquire token silent success
    //                     accessToken = accessTokenResponse.accessToken;
    //                     Logger.debug('interactive',accessToken)
    //                     return accessToken
    //                 })
    //             }
    //             Logger.debug(error);
    //         });
    //
    //     // return authResult.accessToken
    //
    //     // // Silently acquires an access token which is then attached to a request for Microsoft Graph data
    //     // instance.acquireTokenSilent(request).then((response) => {
    //     //     setAccessToken(response.accessToken);
    //     //     Logger.debug('token',response.accessToken)
    //     //     return response.accessToken
    //     // }).catch((e) => {
    //     //     instance.acquireTokenRedirect(request)
    //     // });
    //
    //     // try {
    //     //     let tokenResponse = await authApp.handleRedirectPromise();
    //     //
    //     //     let accountObj;
    //     //     if (tokenResponse) {
    //     //         accountObj = tokenResponse.account;
    //     //     } else {
    //     //         accountObj = authApp.getAllAccounts()[0];
    //     //     }
    //     //
    //     //     if (accountObj && tokenResponse) {
    //     //         Logger.debug("[AuthService.init] Got valid accountObj and tokenResponse")
    //     //     } else if (accountObj) {
    //     //         Logger.debug("[AuthService.init] User has logged in, but no tokens.");
    //     //         try {
    //     //             tokenResponse = await authApp.acquireTokenSilent({
    //     //                 account: authApp.getAllAccounts()[0],
    //     //                 scopes: loginRequest.scopes
    //     //             })
    //     //             Logger.debug(JSON.stringify(tokenResponse))
    //     //         } catch(err) {
    //     //             await authApp.acquireTokenRedirect({scopes: loginRequest.scopes});
    //     //         }
    //     //     } else {
    //     //         Logger.debug("[AuthService.init] No accountObject or tokenResponse present. User must now login.");
    //     //         await authApp.loginRedirect({scopes: loginRequest.scopes})
    //     //     }
    //     // } catch (error) {
    //     //     Logger.error("[AuthService.init] Failed to handleRedirectPromise()", error)
    //     // }
    //
    // }

    const resetPassword = async () => {};
    const updateProfile = () => {};

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <AzureADContext.Provider
            value={{
                ...state,
                logout,
                resetPassword,
                updateProfile,
                verifySession,
                getToken,
                requestToken,
                isAuthenticated,
                attemptRefresh,
            }}
        >
            {children}
        </AzureADContext.Provider>
    );
};

AzureADProvider.propTypes = {
    children: PropTypes.node,
};

export { AzureADContext };
